// src/components/resumo/annotations/blockAnchor.ts
// Sistema de ancoragem robusta de grifos por block-ID + prefix/suffix

import { Highlight } from './annotations.types';

const BLOCK_TAGS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote'];
const CONTEXT_LEN = 30;

const genBlockId = () => `b_${Math.random().toString(36).slice(2, 9)}${Date.now().toString(36)}`;

// TreeWalker filter (same as textHighlighter and rangeOffset to ensure consistent offsets)
const makeFilter = (): NodeFilter => ({
    acceptNode: (node: Node) => {
        const parent = (node as Text).parentElement;
        if (parent?.classList?.contains('highlight-comment-badge')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
    }
});

/** Get text content of a node using same method as textHighlighter */
const getNodeText = (node: HTMLElement): string => {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, makeFilter());
    let text = '';
    let n: Node | null;
    while ((n = walker.nextNode())) text += n.textContent || '';
    return text;
};

/** Calculate absolute start offset of a block within its container */
const getBlockAbsStart = (block: HTMLElement, container: HTMLElement): number => {
    let offset = 0;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, makeFilter());
    let n: Node | null;
    while ((n = walker.nextNode())) {
        if (block.contains(n)) break;
        offset += (n.textContent || '').length;
    }
    return offset;
};

/** Walk up from node to find the nearest block ancestor */
const findParentBlock = (node: Node, container: HTMLElement): HTMLElement | null => {
    let el: Node | null = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (el && el !== container) {
        if (el.nodeType === Node.ELEMENT_NODE) {
            const tag = (el as HTMLElement).tagName.toLowerCase();
            if (BLOCK_TAGS.includes(tag)) return el as HTMLElement;
        }
        el = (el as HTMLElement).parentElement;
    }
    return null;
};

/**
 * Add data-block-id to all block elements that don't have one.
 * Returns modified HTML string.
 */
export const ensureBlockIds = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const selector = BLOCK_TAGS.join(', ');
    temp.querySelectorAll<HTMLElement>(selector).forEach(el => {
        if (!el.dataset.blockId) el.dataset.blockId = genBlockId();
    });
    return temp.innerHTML;
};

/**
 * Capture full anchor info for a highlight being created.
 * Called from useHighlight.ts after computing absoluteStart/End.
 */
export const captureAnchor = (
    range: Range,
    container: HTMLElement,
    absoluteStart: number,
    absoluteEnd: number
): Pick<Highlight, 'blockId' | 'blockStartOffset' | 'blockEndOffset' | 'prefix' | 'suffix'> => {
    const fullText = getNodeText(container);
    const prefix = fullText.slice(Math.max(0, absoluteStart - CONTEXT_LEN), absoluteStart);
    const suffix = fullText.slice(absoluteEnd, absoluteEnd + CONTEXT_LEN);

    // Try to find parent block with a stable ID
    const startBlock = findParentBlock(range.startContainer, container);
    const endBlock = findParentBlock(range.endContainer, container);

    // Only use block anchoring for single-block highlights
    if (startBlock && startBlock === endBlock && startBlock.dataset.blockId) {
        const blockAbsStart = getBlockAbsStart(startBlock, container);
        return {
            blockId: startBlock.dataset.blockId,
            blockStartOffset: absoluteStart - blockAbsStart,
            blockEndOffset: absoluteEnd - blockAbsStart,
            prefix,
            suffix,
        };
    }

    return { prefix, suffix };
};

/** Find text with prefix/suffix context. Returns {start, end} or null. */
const findWithContext = (
    fullText: string,
    text: string,
    prefix: string,
    suffix: string
): { start: number; end: number } | null => {
    // Exact context match
    if (prefix || suffix) {
        const ctx = prefix + text + suffix;
        const idx = fullText.indexOf(ctx);
        if (idx !== -1) return { start: idx + prefix.length, end: idx + prefix.length + text.length };

        // Prefix-guided search
        if (prefix) {
            const pIdx = fullText.indexOf(prefix);
            if (pIdx !== -1) {
                const after = fullText.indexOf(text, pIdx + prefix.length);
                if (after !== -1 && after - pIdx < CONTEXT_LEN * 3)
                    return { start: after, end: after + text.length };
            }
        }

        // Suffix-guided search
        if (suffix) {
            const sIdx = fullText.indexOf(suffix);
            if (sIdx !== -1) {
                const before = fullText.lastIndexOf(text, sIdx);
                if (before !== -1 && sIdx - (before + text.length) < CONTEXT_LEN * 3)
                    return { start: before, end: before + text.length };
            }
        }
    }

    return null;
};

/**
 * Reconcile highlights against the current HTML.
 * Returns { highlights: corrected Highlight[], changed: boolean }.
 * changed=true means at least one offset was corrected (dirty, should persist on next save).
 */
export const reconcileHighlights = (
    highlights: Highlight[],
    html: string
): { highlights: Highlight[]; changed: boolean } => {
    if (!highlights.length) return { highlights, changed: false };

    const temp = document.createElement('div');
    temp.innerHTML = html;
    const fullText = getNodeText(temp);

    let changed = false;

    const reconciled = highlights.map((h): Highlight | null => {
        // ── PATH 1: Block-anchored highlight ──
        if (h.blockId && h.blockStartOffset !== undefined && h.blockEndOffset !== undefined) {
            const block = temp.querySelector(`[data-block-id="${h.blockId}"]`) as HTMLElement | null;

            if (block) {
                const blockAbsStart = getBlockAbsStart(block, temp);
                const blockText = getNodeText(block);
                const actual = blockText.slice(h.blockStartOffset, h.blockEndOffset);

                if (actual === h.text) {
                    // Block intact — update absolute offsets if they drifted
                    const newStart = blockAbsStart + h.blockStartOffset;
                    const newEnd = blockAbsStart + h.blockEndOffset;
                    if (newStart !== h.startOffset || newEnd !== h.endOffset) {
                        changed = true;
                        return { ...h, startOffset: newStart, endOffset: newEnd };
                    }
                    return h;
                }

                // Block text changed — try context within block
                if (h.prefix !== undefined && h.suffix !== undefined) {
                    const m = findWithContext(blockText, h.text, h.prefix, h.suffix);
                    if (m) {
                        changed = true;
                        return {
                            ...h,
                            blockStartOffset: m.start,
                            blockEndOffset: m.end,
                            startOffset: blockAbsStart + m.start,
                            endOffset: blockAbsStart + m.end,
                        };
                    }
                }

                // Try simple text search within block
                const si = blockText.indexOf(h.text);
                if (si !== -1) {
                    changed = true;
                    return {
                        ...h,
                        blockStartOffset: si,
                        blockEndOffset: si + h.text.length,
                        startOffset: blockAbsStart + si,
                        endOffset: blockAbsStart + si + h.text.length,
                    };
                }

                // Block changed too much — fall through to document-wide search
            }
        }

        // ── PATH 2: Verify absolute offsets (legacy or block not found) ──
        const currentText = fullText.slice(h.startOffset, h.endOffset);
        if (currentText === h.text) return h; // still correct

        // ── PATH 3: Context-based document search ──
        if (h.prefix !== undefined && h.suffix !== undefined) {
            const m = findWithContext(fullText, h.text, h.prefix, h.suffix);
            if (m) {
                changed = true;
                return { ...h, startOffset: m.start, endOffset: m.end };
            }
        }

        // ── PATH 4: Simple text search ──
        const fi = fullText.indexOf(h.text);
        if (fi !== -1) {
            changed = true;
            return { ...h, startOffset: fi, endOffset: fi + h.text.length };
        }

        // ── PATH 5: Highlight lost ──
        return null;
    });

    return {
        highlights: reconciled.filter((h): h is Highlight => h !== null),
        changed,
    };
};
